import React, { useEffect } from 'react'

import Overview from '../Overview'
import PositionAndSize from '../PositionAndSize'
import TextOrLayerStyles from '../TextOrLayerStyles'
import Style from '../Style'
import Text from '../Text'
import Appearance from '../Appearance'
import { Corners } from '../Corners'
import Content from '../Content'
import { SymbolInSidebar } from '../SymbolInSidebar'
import Exports from '../Exports'
import { Box, Flex } from '@sketch/components'
import { formatSymbolNameAndPath } from 'modules/shares/Inspector/Sidebar/utils'

import { VersionFragment } from '@sketch/gql-types'
import {
  ColorFormat,
  Layer,
  Rect,
  Appearance as AppearanceType,
  SymbolData,
} from 'modules/shares/types'
import { InspectorData } from 'modules/shares/Inspector/types'

import { trackEvent } from 'modules/analytics'

export interface LayerInfoProps {
  symbolInfo?: SymbolData
  layer: Layer
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  shareIdentifier: string
  symbolDocumentName: string
  currentVersion: VersionFragment
  hasArtboardExportableAssets?: boolean
  textStyles?: InspectorData['textStyles']
  layerStyles?: InspectorData['layerStyles']
  permanentArtboardShortId?: string
}

const LayerInfo: React.FC<LayerInfoProps> = ({
  symbolInfo,
  layer,
  onColorFormatChange,
  colorFormat,
  shareIdentifier,
  currentVersion,
  hasArtboardExportableAssets,
  symbolDocumentName,
  textStyles,
  layerStyles,
  permanentArtboardShortId,
}) => {
  const frame: Rect = layer.frame

  const IsPositionAndSizeVisible = !!frame
  const isCornersVisible = !!layer.cornerRadius?.length
  const isAppearanceVisible = !!layer.style?.appearance?.blendMode
  const isTextVisible = !!layer.attributedString
  const isStyleVisible = !!layer.style
  const isContentVisible = !!layer.attributedString?.string?.length
  const foreignSymbolShareId = symbolInfo?.foreignSymbolShareId
  const foreignVersionShortId = symbolInfo?.foreignVersionShortId

  const [symbolName, path] = formatSymbolNameAndPath(
    symbolDocumentName,
    symbolInfo
  )

  const isSymbolVisible =
    symbolInfo?.symbolData?.name && symbolInfo?.symbolLayer?.id && path !== ''

  useEffect(() => {
    if (isSymbolVisible) {
      trackEvent('CWV - inspect', { type: 'symbols' })
    }
  }, [isSymbolVisible])

  return (
    <>
      <Flex
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        pr={5}
      >
        <Overview layer={layer} />
      </Flex>
      {IsPositionAndSizeVisible && (
        <PositionAndSize
          x={layer.frame.x}
          y={layer.frame.y}
          width={layer.originalFrame.width}
          height={layer.originalFrame.height}
          rotate={layer.rotation}
        />
      )}
      {Object.keys(textStyles || {}).includes(layer.sharedStyleID) ? (
        <TextOrLayerStyles
          key="textStyles"
          shareIdentifier={shareIdentifier}
          currentVersion={currentVersion}
          title="Text Styles"
          layer={layer}
          styles={textStyles!}
        />
      ) : null}
      {Object.keys(layerStyles || {}).includes(layer.sharedStyleID) ? (
        <TextOrLayerStyles
          key="layerStyles"
          shareIdentifier={shareIdentifier}
          currentVersion={currentVersion}
          title="Layer Styles"
          layer={layer}
          styles={layerStyles!}
        />
      ) : null}
      {isCornersVisible && (
        <Corners
          radius={layer.cornerRadius!}
          cornerStyle={layer.cornerStyle}
          smoothCorners={layer.smoothCorners}
        />
      )}
      {isAppearanceVisible && (
        <Appearance {...(layer!.style!.appearance as AppearanceType)} />
      )}
      {isTextVisible && (
        <Text
          attributedString={layer.attributedString!}
          style={layer.style}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
        />
      )}
      {isStyleVisible && (
        <Style
          style={layer.style!}
          onColorFormatChange={onColorFormatChange}
          colorFormat={colorFormat}
          layerType={layer.type}
        />
      )}
      {isContentVisible && <Content>{layer.attributedString!.string}</Content>}
      {isSymbolVisible && (
        <SymbolInSidebar
          name={symbolName}
          symbolArtboard={symbolInfo!.symbolArtboard}
          shareIdentifier={foreignSymbolShareId ?? shareIdentifier}
          foreignVersionShortId={foreignVersionShortId}
          path={path}
          isForeign={symbolInfo?.symbolData?.isForeign}
          permanentArtboardShortId={permanentArtboardShortId}
        />
      )}
      <Box marginTop="auto">
        <Exports
          for={{ type: 'layer', value: layer }}
          currentVersion={currentVersion}
          hasArtboardExportableAssets={hasArtboardExportableAssets}
        />
      </Box>
    </>
  )
}

export default LayerInfo
